import React, { useState, useEffect } from 'react';
import { Search, User, Award, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const CourseCompletionCertificate = () => {
  const [eligibleStudents, setEligibleStudents] = useState([]);
  const [ineligibleStudents, setIneligibleStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [showIneligible, setShowIneligible] = useState(false);
  const [generatingFor, setGeneratingFor] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchEligibleStudents();
  }, []);

  const fetchEligibleStudents = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/fees/course-completion-eligible-students`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibleStudents(response.data.eligible_students);
      setIneligibleStudents(response.data.ineligible_students);
    } catch (error) {
      console.error('Error fetching students:', error);
      alert('Failed to fetch student data');
    } finally {
      setLoading(false);
    }
  };

  const displayStudents = showIneligible ? ineligibleStudents : eligibleStudents;
  
  const filteredStudents = displayStudents.filter(student =>
    student.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.admission_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleGenerateCertificate = async (student) => {
    if (student.balance > 0) {
      alert(`Cannot generate certificate. Student has outstanding balance of ₹${student.balance.toLocaleString('en-IN')}`);
      return;
    }

    setGeneratingFor(student.id);
    try {
      const response = await axios.get(
        `${API_URL}/api/fees/course-completion-certificate/${student.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `course_completion_certificate_${student.admission_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert(error.response?.data?.detail || 'Failed to generate certificate');
    } finally {
      setGeneratingFor(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Course Completion Certificate</h1>
        <p className="text-sm text-gray-500 mt-1">Generate course completion certificates for students who cleared all dues</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">Eligible Students</p>
              <p className="text-3xl font-bold text-green-800 mt-1">{eligibleStudents.length}</p>
            </div>
            <CheckCircle className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-700 font-medium">Ineligible Students</p>
              <p className="text-3xl font-bold text-orange-800 mt-1">{ineligibleStudents.length}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-orange-500" />
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded">
        <div className="flex items-start">
          <Award className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Eligibility:</strong> Student must have completed ALL payments including:
            </p>
            <ul className="list-disc list-inside text-sm text-blue-700 mt-2 ml-2">
              <li>Course Fee (100%)</li>
              <li>Admission Fee & Booking Fee</li>
              <li>Other Fees (if any)</li>
              <li>Hostel Fee (if applicable)</li>
              <li>Transport Fee (if applicable)</li>
              <li>Library Fee (if applicable)</li>
              <li>All Fines</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setShowIneligible(false)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                !showIneligible 
                  ? 'bg-green-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Eligible ({eligibleStudents.length})
            </button>
            <button
              onClick={() => setShowIneligible(true)}
              className={`px-4 py-2 rounded-lg font-medium text-sm ${
                showIneligible 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Ineligible ({ineligibleStudents.length})
            </button>
          </div>
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search students by name or admission number..."
              className="pl-10 pr-4 py-2 w-full border rounded-lg focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student Details</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admission No.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Balance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading students...</td></tr>
            ) : filteredStudents.length === 0 ? (
              <tr><td colSpan="7" className="px-6 py-8 text-center text-gray-500">No students found</td></tr>
            ) : (
              filteredStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{student.full_name}</div>
                        <div className="text-sm text-gray-500">{student.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{student.admission_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{student.course_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{student.session}</td>
                  <td className="px-6 py-4 text-sm text-right font-semibold">
                    <span className={student.balance <= 0 ? 'text-green-600' : 'text-red-600'}>
                      ₹{Math.abs(student.balance).toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {student.balance <= 0 ? (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Completed
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        <XCircle className="h-3 w-3 mr-1" />
                        Pending: ₹{student.balance.toLocaleString('en-IN')}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      onClick={() => handleGenerateCertificate(student)}
                      disabled={student.balance > 0 || generatingFor === student.id}
                      className={`inline-flex items-center gap-1 font-medium text-sm ${
                        student.balance > 0 
                          ? 'text-gray-400 cursor-not-allowed' 
                          : generatingFor === student.id
                            ? 'text-gray-400'
                            : 'text-green-600 hover:text-green-900'
                      }`}
                    >
                      <Download className="h-4 w-4" />
                      {generatingFor === student.id ? 'Generating...' : 'Download Certificate'}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CourseCompletionCertificate;
